import { authStorage } from './storage';
import { GET_USER_INFO_URL } from './constants/url';

/**
 * "이 세션이 아직 살아있는가"를 한 곳에서 판정한다.
 *
 * 판정 근거를 유저 정보 조회 하나로 몰아두는 게 핵심이다. 다른 API 의 5xx 를 곧장
 * "세션이 죽었다"로 읽으면, 백엔드가 잠깐 흔들릴 때마다 멀쩡한 사용자가 전부 튕긴다.
 * 반대로 이 호출은 토큰으로 신원을 확인하는 게 전부라, 여기서 서버가 실패로 답했다면
 * 그 토큰으로는 어차피 아무것도 못 한다.
 *
 * axios 인터셉터(화면 안의 호출이 깨졌을 때)와 SessionGuard(공개 페이지에 머무를 때)가
 * 같은 판정을 써야 한다 — 기준이 갈리면 한쪽은 접었는데 다른 쪽은 로그인 상태로 그린다.
 */

/** 확인 요청이 응답을 못 받으면 '살아있음'으로 본다 — 확신 없이 로그아웃시키지 않는다. */
const probeSession = async (): Promise<boolean> => {
  const accessToken = authStorage.getAccessToken();
  if (!accessToken) return false;

  try {
    // axiosInstance 를 쓰면 응답 인터셉터를 다시 타서 자기 자신을 부른다. 생 fetch 로 나간다.
    const response = await fetch(GET_USER_INFO_URL, {
      headers: { Authorization: `Bearer ${accessToken}` },
      cache: 'no-store',
    });
    return response.ok;
  } catch {
    return true;
  }
};

/**
 * 한 화면이 API 를 여럿 동시에 부르다 같이 5xx 를 맞는 일은 흔하다.
 * 확인 요청을 그 수만큼 쏘지 않도록, 진행 중인 확인이 있으면 그 결과를 같이 쓴다.
 */
let inFlightProbe: Promise<boolean> | null = null;

export const isSessionAlive = (): Promise<boolean> => {
  inFlightProbe ??= probeSession().finally(() => {
    inFlightProbe = null;
  });
  return inFlightProbe;
};

/**
 * 세션을 접는다. 이동은 하지 않는다.
 *
 * 공개 페이지(메인·요금제·블로그)를 보고 있는 사람을 로그인 화면으로 끌고 가면 안 된다 —
 * 그 사람은 로그인이 필요한 일을 하려던 게 아니다. 쿠키만 정리하면 헤더가 '로그인' 버튼을
 * 그리므로, 재로그인 진입점은 생기고 방해는 하지 않는다.
 */
export const endSession = (): void => {
  authStorage.sessionClear();
};

/**
 * 세션을 접고 로그인 화면으로 보낸다.
 *
 * 쿠키를 지우는 게 리다이렉트보다 중요하다. 낡은 쿠키를 남긴 채 /sign-in 으로만 보내면
 * 로그인 폼이 떠도 다음 로그인이 구 쿠키에 가려 같은 상태로 돌아온다.
 * 지우고 나면 로그인 성공 시 심는 값이 유일한 쿠키가 된다.
 *
 * 이미 로그인 화면이면 이동하지 않는다 — 로그인 페이지가 스스로를 다시 부르면 폼이 계속
 * 새로 떠서 정작 로그인을 할 수가 없다.
 */
export const endSessionAndSignIn = (): void => {
  endSession();

  if (typeof window === 'undefined') return;
  if (window.location.pathname.startsWith('/sign-in')) return;

  const returnTo = `${window.location.pathname}${window.location.search}`;
  window.location.href = `/sign-in?redirect=${encodeURIComponent(returnTo)}`;
};
